import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { ProviderMark } from "./icons";
import { API_URL, api } from "../lib/api";

interface SigninProvider {
  id: "github" | "gitlab";
  label: string;
}

/*
 * Fetched once per page load and kept: the list changes when an operator
 * configures an OAuth app, not between two renders. Plain state rather
 * than a query so the sign-in and register pages -- which mount outside
 * the query client -- stay as light as they are.
 */
/** The provider's own colour on its mark; GitHub's is monochrome by design. */
const PROVIDER_TONE: Record<SigninProvider["id"], string> = {
  github: "text-fg",
  gitlab: "text-[#fc6d26]",
};

let cached: SigninProvider[] | null = null;
let inFlight: Promise<SigninProvider[]> | null = null;

function loadProviders(): Promise<SigninProvider[]> {
  if (cached) return Promise.resolve(cached);
  inFlight ??= api<{ data: SigninProvider[] }>("/v1/auth/oauth/providers", { anonymous: true, omitCredentials: true })
    .then((body) => (cached = body.data))
    .catch(() => [] as SigninProvider[])
    .finally(() => (inFlight = null));
  return inFlight;
}

/**
 * "Continue with GitHub / GitLab", on the sign-in and register forms.
 *
 * Anchors, not buttons with handlers: every step of this flow is a
 * top-level navigation -- out to the provider, back to the API, back to
 * the dashboard's `/oauth/signin` -- so the control that starts it is a
 * link, and it works with a middle click and no JavaScript at all. Which
 * providers appear comes from the API, so a deployment with no GitLab app
 * configured shows no GitLab button rather than one that fails.
 *
 * Renders nothing while loading and nothing when none are configured: a
 * divider over an empty row is a layout that lost something.
 */
export function SocialSignIn({
  returnTo,
  mode,
  placement = "below",
}: {
  returnTo: string;
  mode: "signin" | "register";
  /** Above the form, the divider reads "or with email"; below it, "or". */
  placement?: "above" | "below";
}) {
  const { t } = useTranslation("auth");
  const [providers, setProviders] = useState<SigninProvider[]>(cached ?? []);
  useEffect(() => {
    let live = true;
    void loadProviders().then((list) => {
      if (live) setProviders(list);
    });
    return () => {
      live = false;
    };
  }, []);
  if (providers.length === 0) return null;

  const divider = (
    <div className="flex items-center gap-3 text-xs text-fg-subtle" aria-hidden="true">
      <span className="h-px flex-1 bg-line" />
      {t(placement === "above" ? "social.orWithEmail" : "social.or")}
      <span className="h-px flex-1 bg-line" />
    </div>
  );

  return (
    <div className="space-y-3">
      {placement === "below" && divider}
      {/*
       * Stacked and full width, one per provider, the same height as the
       * primary button above them so the column reads as one set of ways
       * in. The mark sits in a fixed slot at the left with the label
       * centred in the rest -- the three-column grid is what keeps the
       * labels aligned across the two buttons whatever their length -- and
       * it wears the provider's own colour, which is what makes a row of
       * these scannable before the words are read. The lift on hover is a
       * border and a fill, not a shadow: the console draws nothing that
       * pretends to float.
       */}
      <div className="grid gap-2">
        {providers.map((provider) => (
          <a
            key={provider.id}
            href={`${API_URL}/v1/auth/oauth/${provider.id}/start?return_to=${encodeURIComponent(returnTo)}`}
            className="group grid h-11 grid-cols-[2.25rem_1fr_2.25rem] items-center rounded-md border border-line bg-surface px-1 text-sm font-medium text-fg transition-[border-color,background-color,transform] duration-200 ease-smooth hover:border-line-strong hover:bg-raised focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/25 focus-visible:outline-none active:translate-y-px pointer-coarse:min-h-11"
          >
            <span className="grid place-items-center">
              <ProviderMark provider={provider.id} className={`size-5 ${PROVIDER_TONE[provider.id]}`} />
            </span>
            <span className="text-center">
              {t(mode === "signin" ? "social.continueWith" : "social.registerWith", { provider: provider.label })}
            </span>
            <span
              aria-hidden="true"
              className="grid place-items-center text-fg-subtle opacity-0 transition-opacity duration-200 ease-smooth group-hover:opacity-100 group-focus-visible:opacity-100"
            >
              →
            </span>
          </a>
        ))}
      </div>
      {mode === "register" && <p className="text-xs leading-5 text-fg-subtle">{t("social.registerNote")}</p>}
      {placement === "above" && divider}
    </div>
  );
}
